import type { Block } from "./models.js";
import { B, D, S, chars, isLetter, isNumber, isUppercase, splitOmitting, trimSet, trimWS } from "./text.js";
import { foldText, matchesGroup } from "./trainingWeek.js";
import type { RecordKind, TargetUnit } from "./types.js";

// WeightsCore's `TrackSession` and `RunName` (DESIGN.md §9.3, §19.1), line for line. The regexes
// are ICU's, with `\b`, `\s` and `\d` spelled as ICU reads them.

function icu(pattern: string, flags: string): RegExp {
  const spelled = pattern.replace(/\\[bsd]/g, (m) => (m === "\\b" ? B : m === "\\s" ? S : D));
  return new RegExp(spelled, flags + "u");
}

function matches(text: string, pattern: string): boolean {
  return icu(pattern, "i").test(text);
}

function replacing(text: string, pattern: string, replacement: string | ((...groups: string[]) => string), flags = "i"): string {
  const regex = icu(pattern, flags + "g");
  return typeof replacement === "string"
    ? text.replace(regex, () => replacement)
    : text.replace(regex, (...args: unknown[]) =>
        replacement(...(args.slice(0, -2) as (string | undefined)[]).map((g) => g ?? "")),
      );
}

/** `PlannedExercise.record`, of the model or of its wire. */
export function recordOf(e: { recordOverride?: RecordKind | null; target?: { unit: TargetUnit } | null; isBodyweight: boolean }): RecordKind {
  if (e.recordOverride != null) return e.recordOverride;
  if (e.target?.unit === "meters") return "time";
  if (e.isBodyweight || e.target?.unit === "seconds") return "reps";
  return "weightReps";
}

/** The text to read. `groups` are the lifter's own column names. */
export function prepare(lines: string[], groups: string[]): string {
  return forGroups(lines, groups)
    .map((line) => asRun(oneTimeTrial(countedFly(line))))
    .flatMap(pairedRuns)
    .flatMap(splitRuns)
    .flatMap(ladder)
    .map((line) => namedRun(nestedReps(line)))
    .join("\n");
}

/** Counted work is ticked; distances keep their optional time and get their run's one name. */
export function adapt(blocks: Block[]): Block[] {
  return blocks.map((original) => {
    const block: Block = { ...original, exercises: original.exercises.map((e) => ({ ...e })) };
    for (const e of block.exercises) {
      const record = recordOf(e);
      if (record === "weightReps" || (record === "reps" && e.target?.unit !== "seconds")) {
        e.recordOverride = "done";
        e.isBodyweight = true;
      }
    }
    for (const e of block.exercises) {
      if (e.target?.unit !== "meters") continue;
      const split = partsIn(e.note);
      if (split !== null) {
        e.parts = split.parts;
        e.note = split.note;
      }
      const run = readRunName(e.name, e.note);
      if (run === null) continue;
      e.name = run.name;
      e.note = run.cue;
      e.isResisted = run.isResisted || (e.isResisted ?? false);
      e.plannedLoad = run.plannedLoad ?? e.plannedLoad ?? null;
    }
    return block;
  });
}

/** One of the day's other sessions with a distance in its lines is a run (DESIGN.md §19.5). */
export function isRunning(lines: string[]): boolean {
  const regex = icu(String.raw`\d\s*m\b`, "");
  return lines.some((line) => regex.test(line));
}

// MARK: Groups

export const groupWords: ReadonlySet<string> = new Set([
  "hep", "hept", "heptathlon", "dec", "deca", "decathlon", "pent", "pentathlon", "multis", "multi",
  "ce", "combined events", "sprints", "sprinters", "jumps", "jumpers", "throws", "throwers",
  "hurdles", "hurdlers", "distance", "men", "women", "m", "w",
]);

/** `Hep: 500m…` and `Dec: 600m…`: only the lifter's own group's line is kept, without its label. */
export function forGroups(lines: string[], groups: string[]): string[] {
  const labelled = lines.map((line): { label: string | null; rest: string } => {
    const colon = line.indexOf(":");
    if (colon < 0) return { label: null, rest: line };
    const label = trimWS(line.slice(0, colon));
    if (!groupWords.has(foldText(label))) return { label: null, rest: line };
    return { label, rest: trimWS(line.slice(colon + 1)) };
  });
  const isMine = (label: string) => groups.some((g) => matchesGroup(label, g));
  if (!labelled.some((entry) => entry.label !== null && isMine(entry.label))) return lines;
  return labelled.flatMap((entry) => (entry.label === null ? [entry.rest] : isMine(entry.label) ? [entry.rest] : []));
}

// MARK: Two runs on one line

/** `600m-90s rest-400m @ 1000m pace` is `1x600m @ 1000m pace` and `1x400m @ 1000m pace`. */
export function splitRuns(line: string): string[] {
  const match = icu(String.raw`^\s*(\d{2,4})\s*m\s*[-–]\s*[^-–]*rest\s*[-–]\s*(\d{2,4})\s*m\b(.*)$`, "i").exec(line);
  if (match === null) return [line];
  const cue = trimWS(match[3] ?? "");
  return [match[1]!, match[2]!].map((m) => trimWS(`1x${m}m ${cue}`));
}

/** `300-200-100m tempo` is three runs, one of each. Two numbers stay a range (`10-15m bounds`). */
export function ladder(line: string): string[] {
  const match = icu(String.raw`^\s*(\d{2,4}(?:\s*m)?(?:\s*[-–]\s*\d{2,4}(?:\s*m)?){2,})\s*m?\b(.*)$`, "i").exec(line);
  if (match === null) return [line];
  const steps = match[1]!;
  const rest = match[2] ?? "";
  if (!/m$/i.test(steps) && !/^m/i.test(rest)) return [line];
  const cue = trimWS(rest.replace(/^\s*m\b/i, ""));
  return steps.split(/[-–]/).map((d) => trimWS(`1x${d.replace(/[\sm]/gi, "")}m ${cue}`));
}

// MARK: Lines the rules cannot read as runs

/** `TT 150m`, `Time trial 300m`: one run, named after. */
export function oneTimeTrial(line: string): string {
  return replacing(line, String.raw`^(\s*)(?:TT|time\s+trial)\s+(\d{2,5})\s*m\b`, (_, a, b) => `${a}1x${b}m time trial`);
}

/** `2x(3x150m) - R: 3/10min` is `2x3x150m - R: 3/10min`: the rules read only the outer count of a bracket of reps. */
export function nestedReps(line: string): string {
  return replacing(line, String.raw`^(\s*)(\d{1,2})\s*[x×]\s*\(\s*(\d{1,2})\s*[x×]\s*(\d{2,4})\s*m\s*\)`, (_, a, b, c, d) => `${a}${b}x${c}x${d}m`);
}

/** `2x3x150m - R: 3/10min`: a distance with only cues after it is a run, not an exercise named for its first cue. */
export function namedRun(line: string): string {
  return replacing(line, String.raw`^(\s*(?:\d{1,2}\s*[x×]\s*){1,2}\d{2,4}\s*m)\s+[-–]\s+`, (_, a) => `${a} run - `);
}

/** `2 fly 30m with 30m run-up`: two flying 30s, not two reps of a drill. */
export function countedFly(line: string): string {
  return replacing(line, String.raw`^(\s*)(\d{1,2})\s+(fly|flying)\s+(\d{2,4})\s*m\b`, (_, a, b, c, d) => `${a}${b}x${d}m ${c}`);
}

/** The rules read a bare `1080` as a number and drop it: as words it stays in the cue. */
export function asRun(line: string): string {
  if (!isRunning([line])) return line;
  return replacing(line, String.raw`(?<![\d.@/]\s?)(?<!the )\b1080\b(?!\s*(m|s|kg|lb|%)\b)`, "on the 1080");
}

const restToken = String.raw`\d+\s*(?:s|sec|secs|min|mins)\.?`;
const partsMarker = "in parts";

/**
 * `2x3x(150m+30s+100) very light tempo - R: 2/6` is 2 rounds of 3 reps of 250 m in parts 150 m and
 * 100 m (DESIGN.md §19.1). The rules read it as `2x3x250m … - 30s between - in parts 150+100`, and
 * `adapt` takes the parts out of the cue.
 */
export function pairedRuns(line: string): string[] {
  const match = icu(
    String.raw`^(\s*)(?:(\d{1,2})\s*[x×]\s*)?(\d{1,2})\s*[x×]\s*\(\s*(\d{2,4}\s*m(?:\s*\+\s*(?:${restToken}|\d{2,4}\s*m?))+)\s*\)\s*(.*)$`,
    "i",
  ).exec(line);
  if (match === null) return [line];
  const parts: number[] = [];
  const rests: string[] = [];
  for (const token of match[4]!.split("+").map((t) => trimWS(t))) {
    if (icu(String.raw`^${restToken}$`, "i").test(token)) {
      const rest = token.replaceAll(" ", "");
      if (!rests.includes(rest)) rests.push(rest);
    } else {
      parts.push(Number.parseInt(token, 10));
    }
  }
  if (parts.length < 2) return [line];
  const indent = match[1] ?? "";
  const rounds = match[2] === undefined ? "" : `${match[2]}x`;
  const reps = match[3]!;
  const total = parts.reduce((a, b) => a + b, 0);
  const written = trimWS(match[5] ?? "");
  // With no name the rules would take the first cue for one.
  const tail = written === "" || written.startsWith("-") ? trimWS(`run ${written}`) : written;
  const cues = rests.length === 0 ? [] : [`${rests.join("/")} between`];
  const dash = tail.indexOf(" - ");
  const said = dash >= 0 ? [tail.slice(0, dash), ...cues, tail.slice(dash + 3)] : [tail, ...cues];
  return [`${indent}${rounds}${reps}x${total}m ${[...said, `${partsMarker} ${parts.join("+")}`].join(" - ")}`.trimEnd()];
}

/** The parts `pairedRuns` left in a cue, and the cue without them. */
export function partsIn(note: string | null): { parts: number[]; note: string | null } | null {
  if (note === null) return null;
  const match = icu(String.raw`(?:^|\s*[-·–—]\s+)${partsMarker}\s+(\d+(?:\+\d+)+)\b`, "i").exec(note);
  if (match === null) return null;
  const rest = trimSet(note.slice(0, match.index) + note.slice(match.index + match[0].length), " \t-–—·:,;.");
  return { parts: match[1]!.split("+").map((p) => Number.parseInt(p, 10)), note: rest === "" ? null : rest };
}

// MARK: RunName

export interface RunName {
  name: string;
  cue: string | null;
  isResisted: boolean;
  plannedLoad: number | null;
}

type Kind = "resisted" | "tempo" | "accel" | "skips" | "timeTrial" | "sprint" | "none";

const kindName: Record<Kind, string | null> = {
  resisted: "Resisted sprint",
  tempo: "Tempo",
  accel: "Accel",
  skips: "Skips",
  timeTrial: "Time trial",
  sprint: "Sprint",
  none: null,
};

const kindPatterns: Record<Kind, string[]> = {
  resisted: [String.raw`\b(resisted|sled)\b`, String.raw`\bsprints?\b`],
  tempo: [String.raw`\btempo\b`],
  accel: [String.raw`\baccel(s|eration|erations)?\b`],
  skips: [String.raw`\bskips?\b`],
  timeTrial: [String.raw`\b(tt|time\s+trials?)\b`],
  sprint: [String.raw`\bsprints?\b`],
  none: [],
};

function kindOf(name: string, cues: string[]): Kind {
  if (matches(name, String.raw`\b(resisted|sled|1080)\b`) || cues.some((c) => matches(c.toLowerCase(), String.raw`\b(sled|1080)\b`))) {
    return "resisted";
  }
  for (const kind of ["tempo", "accel", "skips", "timeTrial", "sprint"] as const) {
    if (kindPatterns[kind].some((p) => matches(name, p))) return kind;
  }
  return "none";
}

const startBlocks = String.raw`\b(from\s+)?(the\s+)?blocks?\b`;
const startFly = String.raw`\b(fly|flying)\b`;
const hill = String.raw`\b(uphill|hills?)\b`;
const notRun = String.raw`\b(push|pushes|carry|carries|lunges?|crawl|crawls|bounds?|bounding|jumps?|hops?)\b`;

export function isRun(name: string): boolean {
  const words = splitOmitting(name, (c) => !isLetter(c) && !isNumber(c));
  if (words.length === 0) return true;
  if (matches(name, notRun)) return false;
  const runWords = [
    String.raw`\b(resisted|sled|1080|tempo|accel(s|eration|erations)?|skips?|tt|time\s+trials?|sprints?|runs?)\b`,
    startBlocks,
    startFly,
    hill,
  ];
  return runWords.some((p) => matches(name, p));
}

/** Nil when the exercise over a distance is not a run: it keeps the name it has. */
export function readRunName(name: string, note: string | null): RunName | null {
  const said = name.toLowerCase();
  if (!isRun(said)) return null;
  let cues = fragments(note);
  const kind = kindOf(said, cues);

  let rest = ` ${name} `;
  for (const pattern of [...kindPatterns[kind], startBlocks, startFly, hill, String.raw`\bruns?\b`]) {
    rest = replacing(rest, pattern, " ");
  }
  const leftover = lowercasedFirst(tidy(rest));

  let fromBlocks = matches(said, startBlocks);
  let flying = matches(said, startFly);
  let uphill = matches(said, hill);
  cues = cues.flatMap((cue) => {
    const folded = cue.toLowerCase();
    if (["fly", "flying", "flying start"].includes(folded)) {
      flying = true;
      return [];
    }
    if (["uphill", "hill", "hills", "on a hill", "on the hill"].includes(folded)) {
      uphill = true;
      return [];
    }
    if (matches(folded, startBlocks)) {
      fromBlocks = true;
      const left = tidy(replacing(cue, String.raw`(\band\s+)?\b(from\s+)?(the\s+)?blocks?\b`, " "));
      return left === "" ? [] : [left];
    }
    return [cue];
  });

  let base = kindName[kind] ?? (fromBlocks || flying ? "Sprint" : "Run");
  if (flying) base = "Flying " + base.toLowerCase();
  else if (fromBlocks) base += " from blocks";
  if (uphill) base += " uphill";

  let all = [...(leftover === "" ? [] : [leftover]), ...cues];
  let load: number | null = null;
  if (kind === "resisted") {
    all = all.flatMap((cue) => {
      if (load !== null) return [cue];
      const found = loadIn(cue);
      if (found === null) return [cue];
      load = found.kilograms;
      return found.rest === "" ? [] : [found.rest];
    });
  }
  const seen = new Set<string>();
  all = all.filter((cue) => {
    const key = cue.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const ordered = [...all.filter((c) => !isRest(c)), ...all.filter(isRest)];
  return {
    name: base,
    cue: ordered.length === 0 ? null : ordered.join(" · "),
    isResisted: kind === "resisted",
    plannedLoad: load,
  };
}

/** `@ 90% - R: 2/8 · walk-in start` is `@ 90%`, `rest 2/8` and `walk-in start`. */
export function fragments(note: string | null): string[] {
  if (note === null) return [];
  const restLead = icu(String.raw`^\s*R\s*:\s*`, "g");
  return note
    .split(" · ")
    .flatMap((a) => a.split(" - ").flatMap((b) => b.split(" – ")).flatMap((c) => c.split(" — ")))
    .map((cue) => cue.replace(restLead, () => "rest "))
    .map(tidy)
    .filter((cue) => cue !== "");
}

function isRest(cue: string): boolean {
  return matches(cue.toLowerCase(), String.raw`\b(rest|recovery)\b`);
}

/** `@ 6kg`, `6 kg`, `6kg`: the planned load, and what is left of the cue. */
export function loadIn(cue: string): { kilograms: number; rest: string } | null {
  const match = icu(String.raw`(@\s*)?(\d+(?:[.,]\d+)?)\s*kgs?\b`, "i").exec(cue);
  if (match === null) return null;
  const kilograms = Number(match[2]!.replace(",", "."));
  if (!Number.isFinite(kilograms)) return null;
  const rest = cue.slice(0, match.index) + cue.slice(match.index + match[0].length);
  return { kilograms, rest: tidy(rest) };
}

/** `On the 1080` was sentence-cased as a name; as a cue it is not. `MB` is left as it is. */
function lowercasedFirst(text: string): string {
  const list = chars(text);
  const first = list[0];
  if (first === undefined || !isUppercase(first) || isUppercase(list[1])) return text;
  return first.toLowerCase() + list.slice(1).join("");
}

function tidy(text: string): string {
  return trimSet(replacing(text, String.raw`\s{2,}`, " ", ""), " \t-–—:,;.");
}
